import { useCallback, useEffect } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { apiFetch, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { WorkspaceProvider, Workspace, CreditCosts } from '@/contexts/WorkspaceContext';
import { WorkspaceRole } from '@/lib/permissions';
import { Entitlements } from '@/lib/plans';

interface WorkspaceResponse {
  workspace: Workspace;
  role: WorkspaceRole;
  permissions: string[];
  isSuperAdmin: boolean;
  entitlements: Entitlements;
  creditCosts?: CreditCosts;
}

// The workspace — and with it the credit balance every page reads — used to be fetched once per
// workspace and never again, so a balance went stale the moment anything spent credits. It is a
// query now: refetched on window focus and every minute, and on demand through refreshWorkspace()
// after a launch, a generation or a test send.
const REFRESH_INTERVAL_MS = 60 * 1000;

export default function WorkspaceLayout() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const queryClient = useQueryClient();

  const { data, isPending, error } = useQuery({
    queryKey: queryKeys.workspace(workspaceId as string),
    queryFn: () => apiFetch<WorkspaceResponse>('/workspaces/' + workspaceId),
    enabled: !!workspaceId,
    refetchOnWindowFocus: true,
    refetchInterval: REFRESH_INTERVAL_MS,
    // A 403 here means "not your workspace"; retrying it only delays the redirect.
    retry: false,
  });

  useEffect(() => {
    if (!data) return;
    localStorage.setItem('workspaceId', data.workspace.id);
    // Cache permissions so the Navbar can role-filter links on workspace-agnostic
    // pages (Profile/Support) where there's no WorkspaceProvider context.
    localStorage.setItem('workspacePermissions', JSON.stringify(data.permissions || []));
  }, [data]);

  const refreshWorkspace = useCallback(() => {
    if (!workspaceId) return;
    queryClient.invalidateQueries({ queryKey: queryKeys.workspace(workspaceId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard(workspaceId) });
  }, [queryClient, workspaceId]);

  // Only the FIRST load blocks the page. A background refetch keeps what is on screen, and one that
  // fails keeps the last good data rather than throwing the user out of their workspace.
  //
  // A 401 also waits: by the time it arrives here the session has been renewed (and the request
  // retried) or found to be over and cleared, and ProtectedRoute is already redirecting to /login.
  // Bouncing it through /app as well would only race that redirect.
  if (isPending || (!data && error instanceof ApiError && error.status === 401)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // No data after the first load is a refusal (not this user's workspace) or a failure.
  if (!data) {
    return <Navigate to="/app" replace />;
  }

  return (
    <WorkspaceProvider
      workspace={data.workspace}
      workspaceId={workspaceId as string}
      role={data.role}
      permissions={data.permissions}
      isSuperAdmin={data.isSuperAdmin}
      entitlements={data.entitlements}
      creditCosts={data.creditCosts}
      refreshWorkspace={refreshWorkspace}
    >
      <Outlet />
    </WorkspaceProvider>
  );
}
